import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Segment } from '~src/interfaces/segment.interface';
import SelectInput from '~src/shared/form/select/SelectInput';
import { VisibilityOptions, visibilityOptions } from '~src/modules/okrs/constants/options';
import { Visibility } from '~src/modules/okrs/manageObjectives/shared/shared.sc';

import CustomVisibilityForm from './CustomVisibilityForm';

export interface VisibilityObj {
  key: VisibilityOptions;
  people?: Segment[];
  shareWithManagers?: boolean;
}

interface Props {
  value: VisibilityObj;
  onChange: (val: VisibilityObj) => void;
  label: string;
  error: string | null;
}

const isVisibilityOption = (opt: string): opt is VisibilityOptions =>
  Boolean(Object.values(VisibilityOptions).find(val => String(val) === opt));

const VisibilitySelector: FC<Props> = props => {
  const [t] = useTranslation();
  const [isVisible, setIsVisible] = useState(props.value.key === VisibilityOptions.custom);
  const visibilities = useMemo(
    () =>
      visibilityOptions.map(option => ({
        name: t(`x.okr.objective.form.visibility.${option}`),
        value: option,
      })),
    [],
  );

  const onSelectOption = (option: string) => {
    if (!isVisibilityOption(option)) return;

    setIsVisible(option === VisibilityOptions.custom);

    props.onChange({ key: option });
  };

  const onSelectCustom = ({
    people,
    shareWithManagers,
  }: {
    people: Segment[];
    shareWithManagers: boolean;
  }) => {
    props.onChange({ key: VisibilityOptions.custom, people, shareWithManagers });
  };

  return (
    <Visibility>
      <SelectInput
        t={t}
        value={props.value.key}
        label={props.label}
        optList={visibilities}
        labelKey={props.label}
        onChange={onSelectOption}
      />

      {isVisible ? (
        <CustomVisibilityForm
          selectedPeople={props.value.people || []}
          managersChecked={props.value.shareWithManagers}
          onChange={onSelectCustom}
        />
      ) : null}
    </Visibility>
  );
};

export default VisibilitySelector;
